import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateInvoiceMutation,
  useInvoiceConsignmentContractsLookupQuery,
  useInvoicePContractsLookupQuery,
  useInvoicePriceUnitsLookupQuery,
  useInvoiceWarehousesLookupQuery,
  useLazyGetInvoiceQuery,
  useLazyInvoiceContractSnapshotQuery,
  useUpdateInvoiceMutation,
} from '@/store/api/procurementApi'
import type {
  InvoiceContractSnapshot,
  InvoiceDetail,
  InvoiceLookupItem,
  InvoiceWrite,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (ListView imports this).
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  invoiceId: number | null
  canEdit: boolean
  /** Prefill hợp đồng khi mở mode New (Access: PayDataAdd btnAddInvoice set
   *  p_contract sẵn vào form Invoice Add). */
  initialContractNo?: string | null
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Draft state. Numbers stored as formatted strings ("1,234.56") so inputs can
// show thousand separators (Access Format = Standard).
// ---------------------------------------------------------------------------
interface DraftState {
  invoice_id: string
  invoice_no: string
  invoice_date: string
  // contract routing (mutual exclusion)
  p_contract: string
  consignment_contract: string
  // read-only DLookup (contract snapshot)
  vendor: string
  quality: string
  quantity_snap: string
  bag_snap: string
  outright_snap: string
  ptbf_snap: string
  unit_snap: string
  month_snap: string
  vat_txt: string
  // editable amounts
  quantity: string
  price_vnd_kg: string
  price_unit: string
  total_payment: string
  vat_amount: string
  final_payment: string
  warehouse: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  invoice_id: '',
  invoice_no: '',
  invoice_date: '',
  p_contract: '',
  consignment_contract: '',
  vendor: '',
  quality: '',
  quantity_snap: '',
  bag_snap: '',
  outright_snap: '',
  ptbf_snap: '',
  unit_snap: '',
  month_snap: '',
  vat_txt: '',
  quantity: '',
  price_vnd_kg: '',
  price_unit: 'VND/KG',
  total_payment: '',
  vat_amount: '',
  final_payment: '',
  warehouse: '',
  note: '',
}

// ---------------------------------------------------------------------------
// Numeric helpers (Standard → thousand sep + 2dp).
// ---------------------------------------------------------------------------
function parseNumeric(v: string | number | null | undefined): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/** A contract_no starting with NPE → consignment; else P contract. */
function isNpe(contractNo: string | null | undefined): boolean {
  return String(contractNo ?? '').toUpperCase().startsWith('NPE')
}

type Opt = InvoiceLookupItem
const SPAN2: SxProps<Theme> = { gridColumn: { xs: '1', sm: '1 / span 2' } }
const COL1: SxProps<Theme> = { gridColumn: { xs: '1', sm: '1' } }
const COL2: SxProps<Theme> = { gridColumn: { xs: '1', sm: '2' } }

/**
 * Procurement Invoice New / Edit / Copy dialog — converts Access form
 * `frmProcurementQryInvoiceAdd` (Caption "Procurement - Invoice",
 * btnNew/btnEdit/btnCopy).
 *
 * Layout follows the Access 2-column grid (mobile stacks to 1 column, no
 * fullScreen). Numeric fields right-aligned Standard; date DatePicker
 * DD-MMM-YYYY. Combos are flat MUI Autocomplete "CODE — NAME".
 *
 * P Contract / NPE Contract are mutually exclusive: choosing one clears the
 * other + all read-only DLookup fields, then refetches the contract snapshot.
 */
export default function ProcurementInvoiceFormDialog({
  open,
  mode,
  invoiceId,
  canEdit,
  initialContractNo = null,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [hydrating, setHydrating] = useState(mode !== 'new')
  // vat_rate resolved from chosen contract snapshot (P → local_p.vat_rate,
  // NPE → mua_vat; the snapshot endpoint returns the right one).
  const [vatRate, setVatRate] = useState<number>(0)

  // Mutations
  const [createInvoice, { isLoading: creating }] = useCreateInvoiceMutation()
  const [updateInvoice, { isLoading: updating }] = useUpdateInvoiceMutation()
  const saving = creating || updating

  // Lazy detail + snapshot
  const [fetchDetail] = useLazyGetInvoiceQuery()
  const [fetchSnapshot] = useLazyInvoiceContractSnapshotQuery()

  // Lookups
  const { data: pContractsData } = useInvoicePContractsLookupQuery()
  const { data: npeContractsData } = useInvoiceConsignmentContractsLookupQuery()
  const { data: warehousesData } = useInvoiceWarehousesLookupQuery()
  const { data: priceUnitsData } = useInvoicePriceUnitsLookupQuery()

  const pContractOptions = useMemo<Opt[]>(() => pContractsData?.items ?? [], [pContractsData])
  const npeContractOptions = useMemo<Opt[]>(() => npeContractsData?.items ?? [], [npeContractsData])
  const warehouseOptions = useMemo<Opt[]>(() => warehousesData?.items ?? [], [warehousesData])
  const priceUnitOptions = useMemo<Opt[]>(() => priceUnitsData?.items ?? [], [priceUnitsData])

  const npeMode = !!draft.consignment_contract && !draft.p_contract

  // -------------------------------------------------------------------------
  // Apply a contract snapshot → fill read-only DLookup fields + vat_rate.
  // -------------------------------------------------------------------------
  const applySnapshot = async (contractNo: string) => {
    if (!contractNo) {
      setVatRate(0)
      return
    }
    try {
      const res = await fetchSnapshot({ contractNo }).unwrap()
      const s: InvoiceContractSnapshot = res.data
      setVatRate(parseNumeric(s.vat_rate as number | null | undefined) ?? 0)
      setDraft((prev) => ({
        ...prev,
        vendor: String(s.vendor ?? ''),
        quality: String(s.quality_code ?? ''),
        quantity_snap: formatStandard(s.quantity_kg),
        bag_snap: formatStandard(s.no_of_bag),
        outright_snap: formatStandard(s.outright_price),
        ptbf_snap: formatStandard(s.ptbf_diffs),
        unit_snap: String(s.price_unit ?? ''),
        month_snap: String(s.futures_month ?? ''),
        vat_txt: String(s.vat_txt ?? ''),
      }))
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to load contract info'))
    }
  }

  // -------------------------------------------------------------------------
  // Hydrate on open. Deps strictly [open, mode, invoiceId].
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    let cancelled = false

    const init = async () => {
      // New: empty, today's date, price unit default. Prefill contract nếu
      // parent truyền (PayDataAdd btnAddInvoice) → chạy snapshot như user chọn.
      if (mode === 'new' || invoiceId == null) {
        const initCn = (initialContractNo ?? '').trim()
        const initNpe = initCn ? isNpe(initCn) : false
        setDraft({
          ...EMPTY_DRAFT,
          invoice_date: new Date().toISOString().slice(0, 10),
          p_contract: initCn && !initNpe ? initCn : '',
          consignment_contract: initCn && initNpe ? initCn : '',
        })
        setVatRate(0)
        if (initCn) void applySnapshot(initCn)
        return
      }
      // Edit / Copy: load detail.
      setHydrating(true)
      try {
        const res = await fetchDetail(invoiceId).unwrap()
        if (cancelled) return
        const row: InvoiceDetail = res.data
        const cn = String(row.contract_no ?? '')
        const npe = isNpe(cn)
        if (mode === 'copy') {
          // btnCopy copies fewer fields: invoice_no, invoice_date, warehouse,
          // note + contract routing. Amounts blank; invoice_id blank.
          setDraft({
            ...EMPTY_DRAFT,
            invoice_no: String(row.invoice_no ?? ''),
            invoice_date: String(row.invoice_date ?? '').slice(0, 10),
            warehouse: String(row.warehouse ?? ''),
            note: String(row.note ?? ''),
            p_contract: npe ? '' : cn,
            consignment_contract: npe ? cn : '',
          })
        } else {
          // Edit: full hydrate.
          const total = parseNumeric(row.total_payment) ?? 0
          const vat = parseNumeric(row.vat_amount) ?? 0
          setDraft({
            ...EMPTY_DRAFT,
            invoice_id: row.invoice_id != null ? String(row.invoice_id) : '',
            invoice_no: String(row.invoice_no ?? ''),
            invoice_date: String(row.invoice_date ?? '').slice(0, 10),
            warehouse: String(row.warehouse ?? ''),
            note: String(row.note ?? ''),
            p_contract: npe ? '' : cn,
            consignment_contract: npe ? cn : '',
            quantity: formatStandard(row.quantity),
            price_vnd_kg: formatStandard(row.price_vnd_kg),
            vat_amount: formatStandard(row.vat_amount),
            total_payment: formatStandard(row.total_payment),
            final_payment: formatStandard(total + vat),
          })
        }
        if (cn) await applySnapshot(cn)
      } catch (err) {
        if (!cancelled) snack.error(getErrorMessage(err, 'Failed to load invoice'))
      } finally {
        if (!cancelled) setHydrating(false)
      }
    }

    void init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, invoiceId])

  // -------------------------------------------------------------------------
  // Contract picks — mutual exclusion + clear read-only fields + refetch.
  // -------------------------------------------------------------------------
  const clearSnapshotFields = (prev: DraftState): DraftState => ({
    ...prev,
    vendor: '',
    quality: '',
    quantity_snap: '',
    bag_snap: '',
    outright_snap: '',
    ptbf_snap: '',
    unit_snap: '',
    month_snap: '',
    vat_txt: '',
  })

  const handlePickPContract = (value: string) => {
    setVatRate(0)
    setDraft((prev) => ({ ...clearSnapshotFields(prev), p_contract: value, consignment_contract: '' }))
    if (value) void applySnapshot(value)
  }

  const handlePickNpeContract = (value: string) => {
    setVatRate(0)
    setDraft((prev) => ({ ...clearSnapshotFields(prev), consignment_contract: value, p_contract: '' }))
    if (value) void applySnapshot(value)
  }

  // -------------------------------------------------------------------------
  // Field setters + computed cascade.
  // -------------------------------------------------------------------------
  const set = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  /** Recompute total_payment, vat_amount, final_payment from quantity/price. */
  const recomputeFromAmounts = (quantityStr: string, priceStr: string) => {
    setDraft((prev) => {
      const q = parseNumeric(quantityStr) ?? 0
      const p = parseNumeric(priceStr) ?? 0
      const total = Math.round(q * p)
      const vat = Math.round(total * vatRate)
      const final = Math.round(total) + Math.round(vat)
      return {
        ...prev,
        quantity: quantityStr,
        price_vnd_kg: priceStr,
        total_payment: formatStandard(total),
        vat_amount: formatStandard(vat),
        final_payment: formatStandard(final),
      }
    })
  }

  const handleQuantityChange = (v: string) => recomputeFromAmounts(v, draft.price_vnd_kg)
  const handlePriceChange = (v: string) => recomputeFromAmounts(draft.quantity, v)

  /** vat_amount override → recompute final only. */
  const handleVatChange = (v: string) => {
    setDraft((prev) => {
      const total = Math.round(parseNumeric(prev.total_payment) ?? 0)
      const vat = Math.round(parseNumeric(v) ?? 0)
      return { ...prev, vat_amount: v, final_payment: formatStandard(total + vat) }
    })
  }

  const handleNumericBlur = (key: keyof DraftState) => () => {
    setDraft((prev) => {
      const formatted = formatStandard(prev[key])
      if (formatted === prev[key]) return prev
      return { ...prev, [key]: formatted }
    })
  }

  const handleInvoiceNoBlur = () =>
    setDraft((prev) => ({ ...prev, invoice_no: prev.invoice_no.toUpperCase() }))

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canEdit || saving || hydrating || savingRef.current) return

    // Validation (verbatim English, ordered).
    if (!draft.invoice_no.trim()) {
      snack.warning('Please type Invoice No!')
      return
    }
    if (!draft.invoice_date) {
      snack.warning('Please choose Invoice Date!')
      return
    }
    if (draft.p_contract && draft.consignment_contract) {
      snack.warning('Please choose only P contract or Consignment!')
      return
    }
    if (parseNumeric(draft.total_payment) == null) {
      snack.warning('Please type Total Payment!')
      return
    }

    savingRef.current = true
    try {
      const contractNo = (draft.p_contract || draft.consignment_contract || '').trim()
      const body: InvoiceWrite = {
        invoice_no: draft.invoice_no.toUpperCase().trim(),
        invoice_date: draft.invoice_date || null,
        contract_no: contractNo || null,
        quantity: parseNumeric(draft.quantity),
        price_vnd_kg: parseNumeric(draft.price_vnd_kg),
        total_payment: parseNumeric(draft.total_payment),
        vat_amount: parseNumeric(draft.vat_amount),
        warehouse: draft.warehouse || null,
        note: draft.note || null,
        is_new: mode !== 'edit',
      }

      if (mode === 'edit' && invoiceId != null) {
        await updateInvoice({ id: invoiceId, body }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createInvoice(body).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save invoice'))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const titleSuffix =
    mode === 'new' ? 'Invoice - New' : mode === 'copy' ? 'Invoice - Copy' : 'Invoice - Edit'
  const titleText = `Inbound Logistics - ${titleSuffix}`

  const canSave = canEdit && !saving && !hydrating

  /** Flat Autocomplete for a {value,label} lookup; injects the saved value. */
  const renderCombo = (params: {
    label: string
    value: string
    options: Opt[]
    onPick: (v: string) => void
    sx?: SxProps<Theme>
    disabled?: boolean
    collapseEqual?: boolean
  }) => {
    const { label, value, options, onPick, sx, disabled, collapseEqual } = params
    const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : null)
    const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
    return (
      <Autocomplete
        size="small"
        sx={sx}
        options={merged}
        value={sel}
        disabled={disabled}
        onChange={(_e, v) => onPick(v?.value ?? '')}
        getOptionLabel={(o) => {
          if (!o) return ''
          const code = String(o.value ?? '')
          const lab = String(o.label ?? '')
          if (!lab || lab === code) return code
          if (collapseEqual && code === lab) return code
          return `${code} — ${lab}`
        }}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(p) => <TextField {...p} label={label} />}
      />
    )
  }

  /** Read-only display TextField (shaded) driven by an explicit value string. */
  const roFieldVal = (value: string, label: string, sx?: SxProps<Theme>, num = false) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={num ? numericInputProps : undefined}
      sx={[roSx, ...(num ? [numSx] : []), ...(sx ? [sx] : [])] as SxProps<Theme>}
    />
  )

  /** Editable numeric field. */
  const numField = (
    key: keyof DraftState,
    label: string,
    sx?: SxProps<Theme>,
    onChange?: (v: string) => void,
    required = false,
    error = false,
  ) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      type="text"
      value={draft[key]}
      onChange={(e) => (onChange ? onChange(e.target.value) : set(key, e.target.value))}
      onBlur={handleNumericBlur(key)}
      disabled={!canEdit}
      required={required}
      error={error}
      inputProps={numericInputProps}
      sx={[numSx, ...(sx ? [sx] : [])] as SxProps<Theme>}
    />
  )

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title={titleText} sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Inbound Logistics - </Box>{titleSuffix}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {hydrating ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Invoice.
          </Alert>
        )}

        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 — invoice_id (RO) | (empty) */}
          <TextField
            label="Invoice ID"
            size="small"
            fullWidth
            value={draft.invoice_id}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[roSx, COL1] as SxProps<Theme>}
          />

          {/* Row 2 — invoice_no | invoice_date */}
          <TextField
            label="Invoice No"
            size="small"
            fullWidth
            value={draft.invoice_no}
            onChange={(e) => set('invoice_no', e.target.value)}
            onBlur={handleInvoiceNoBlur}
            disabled={!canEdit || isEdit}
            required
            error={!draft.invoice_no}
            sx={COL1}
          />
          <Box sx={COL2}>
            <DatePicker
              label="Invoice Date"
              format="DD-MMM-YYYY"
              disabled={!canEdit}
              value={draft.invoice_date ? dayjs(draft.invoice_date) : null}
              onChange={(d) => set('invoice_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  required: true,
                  error: !draft.invoice_date,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Box>

          {/* Row 3 — p_contract | consignment_contract (mutual exclusion) */}
          {renderCombo({
            label: 'P Contract',
            value: draft.p_contract,
            options: pContractOptions,
            onPick: handlePickPContract,
            disabled: !canEdit || isEdit,
            sx: COL1,
          })}
          {renderCombo({
            label: 'NPE Contract',
            value: draft.consignment_contract,
            options: npeContractOptions,
            onPick: handlePickNpeContract,
            disabled: !canEdit || isEdit,
            sx: COL2,
          })}

          {/* Read-only DLookup fields — Access 2 cột cố định: P Contract (trái) /
              Consignment (phải), cùng hiển thị. Cột nào không phải loại contract
              đang chọn thì để trống (giống DLookup Access trên contract rỗng).
              Vendor để full-width mỗi cột (giá trị dài); các field còn lại của cột
              P Contract nén 2/hàng trong PHẠM VI cột trái, không lấn cột NPE. */}
          {/* Vendor */}
          {roFieldVal(npeMode ? '' : draft.vendor, 'P Contract - Vendor', COL1)}
          {roFieldVal(npeMode ? draft.vendor : '', 'Consignment - Vendor', COL2)}

          {/* P Contract — box nén 2/hàng (COL1) */}
          <Box sx={[COL1, FORM_GRID_2COL] as SxProps<Theme>}>
            {roFieldVal(npeMode ? '' : draft.quality, 'P Contract - Quality')}
            {roFieldVal(npeMode ? '' : draft.quantity_snap, 'P Contract - Quantity', undefined, true)}
            {roFieldVal(npeMode ? '' : draft.bag_snap, 'P Contract - Bag', undefined, true)}
            {roFieldVal(npeMode ? '' : draft.unit_snap, 'P Contract - Price Unit')}
            {roFieldVal(npeMode ? '' : draft.outright_snap, 'P Contract - Outright', undefined, true)}
            {roFieldVal(npeMode ? '' : draft.ptbf_snap, 'P Contract - PTBF', undefined, true)}
            {roFieldVal(npeMode ? '' : draft.month_snap, 'P Contract - Futures Month')}
            {roFieldVal(npeMode ? '' : draft.vat_txt, 'P Contract - Appliable VAT')}
          </Box>

          {/* Consignment — giữ nguyên, stack trong cột NPE (COL2) */}
          <Box
            sx={[
              COL2,
              { display: 'grid', gridTemplateColumns: '1fr', rowGap: 1.25, alignItems: 'start' },
            ] as SxProps<Theme>}
          >
            {roFieldVal(npeMode ? draft.quality : '', 'Consignment - Quality')}
            {roFieldVal(npeMode ? draft.quantity_snap : '', 'Consignment - Quantity', undefined, true)}
          </Box>

          {/* quantity | price */}
          {numField('quantity', 'Quantity', COL1, handleQuantityChange)}
          {numField('price_vnd_kg', 'Price', COL2, handlePriceChange)}

          {/* price_unit (combo, NOT saved) */}
          {renderCombo({
            label: 'Price Unit',
            value: draft.price_unit,
            options: priceUnitOptions.length
              ? priceUnitOptions
              : [{ value: 'VND/KG', label: 'VND/KG' }],
            onPick: (v) => set('price_unit', v),
            disabled: !canEdit,
            collapseEqual: true,
            sx: COL1,
          })}

          {/* total_payment | vat_amount */}
          {numField(
            'total_payment',
            'Total Amount',
            COL1,
            (v) => set('total_payment', v),
            true,
            !draft.total_payment,
          )}
          {numField('vat_amount', 'VAT Amount', COL2, handleVatChange)}

          {/* final_payment (NOT saved) | warehouse */}
          {numField('final_payment', 'Total Amount after tax', COL1, (v) => set('final_payment', v))}
          {renderCombo({
            label: 'Warehouse',
            value: draft.warehouse,
            options: warehouseOptions,
            onPick: (v) => set('warehouse', v),
            disabled: !canEdit,
            sx: COL2,
          })}

          {/* note (full width) */}
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => set('note', e.target.value)}
            disabled={!canEdit}
            sx={SPAN2}
          />
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
